import { dayHead, monthHead } from '../design/dates';
import { ulidTime } from '../entity/catalog';
import type { Arc } from './arc';

/**
 * The plan as a chronology — every arc once, newest first, storylines and
 * their members side by side in the same run.
 *
 * This is the one reading that does NOT go through `buildBoard`. Nesting,
 * repeated heads and the member cap are all that function's doing, and here
 * they would be in the way: a storyline is an arc that was touched on a day
 * like any other, and its members were each touched on days of their own.
 * Status stops sorting anything and rides the row's own glyph instead.
 */

export interface HeadedArc {
  /** Set on the first arc under a new head; null under the same one. */
  head: string | null;
  arc: Arc;
}

/** Day heads this far back, month heads beyond — the rule the thoughts feed
 *  already runs on, so the two chronologies read alike. */
const DAY_HEADS_WITHIN = 14 * 86_400_000;

/**
 * When an arc was minted, from its `id`.
 *
 * Ids are ULIDs, so the millisecond is already in the file and no arc needs
 * a `created:` key to be placed in time.
 */
export function mintedAt(arc: Arc): number {
  return ulidTime(String(arc.frontMatter['id'] ?? '')) ?? 0;
}

/**
 * The day `updated:` names, taken as its END.
 *
 * The key carries a date and no time, so a choice has to be made about where
 * in the day it sits, and the end is the only one that never inverts a real
 * edit: an arc updated today would otherwise sort below an arc merely minted
 * this morning.
 */
export function updatedAt(arc: Arc): number {
  const m = String(arc.frontMatter['updated'] ?? '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return 0;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 23, 59, 59, 999).getTime();
}

/** Created or updated, whichever is later. Zero for an arc with neither,
 *  which sorts to the foot rather than to 1970. */
export function recencyOf(arc: Arc): number {
  return Math.max(mintedAt(arc), updatedAt(arc));
}

/**
 * Newest first, with a head wherever the day (or the month, further back)
 * changes.
 *
 * Ties are the common case rather than the edge: `updated:` has day
 * precision, so everything touched on one day arrives with the same key and
 * the mint time is what orders it — newest arc first, which is the reading
 * that matches the head above it.
 */
export function chronological(arcs: readonly Arc[], now = new Date()): HeadedArc[] {
  const sorted = [...arcs].sort(
    (a, b) =>
      recencyOf(b) - recencyOf(a) || mintedAt(b) - mintedAt(a) || a.title.localeCompare(b.title),
  );

  let last: string | null = null;
  return sorted.map((arc) => {
    const label = headFor(recencyOf(arc), now);
    const head = label === last ? null : label;
    last = label;
    return { head, arc };
  });
}

function headFor(at: number, now: Date): string {
  if (at === 0) return 'Undated';
  const when = new Date(at);
  return now.getTime() - at <= DAY_HEADS_WITHIN ? dayHead(when, now) : monthHead(when);
}
